import React, { FC, memo, useEffect, useState } from 'react'
import { Pagination } from 'transfix-ui/components/Pagination'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Table, TableRow, TableCell, TableBody } from 'transfix-ui/components/Table'
import { Box } from 'transfix-ui/core'
import { useGraphQLPagination } from 'utils/hooks/useGraphQLPagination'
import { MatchingShipmentRow } from './MatchingShipmentRow'
import { MatchingShipmentsFilters } from './MatchingShipmentsFilters'
import MatchingShipmentsTableHeader from './MatchingShipmentsTableHeader'
import {
  IShipmentListForCarrierQuery,
  IShipmentListForCarrierQueryVariables,
  useShipmentListForCarrierLazyQuery,
} from './graphql/MatchingShipmentsList'
import { IShipmentMatchFragment } from './graphql/ShipmentMatchFragment'
import {
  IMatchingShipmentsFilters,
  useMatchingShipmentsFilters,
} from './hooks/useMatchingShipmentsFilters'

const PER_PAGE = 15

interface IMatchingShipmentListProps {
  carrierId: string
}

export const MatchingShipmentsList: FC<IMatchingShipmentListProps> = ({ carrierId }) => {
  const [
    fetchInitial,
    { data, fetchMore, loading: loadingInitial },
  ] = useShipmentListForCarrierLazyQuery({
    variables: {
      carrierId,
      first: PER_PAGE,
    },
    notifyOnNetworkStatusChange: true,
  })

  const { shipmentListForCarrier: result } = data ?? {}
  const { edges, pageInfo, totalCount = 0 } = result ?? {}

  const { filters, setFilter } = useMatchingShipmentsFilters()

  const {
    currentPage,
    handlePageChange,
    paginatedNodes: paginatedShipments,
    totalPages,
  } = useGraphQLPagination<
    IShipmentMatchFragment,
    IShipmentListForCarrierQuery,
    IShipmentListForCarrierQueryVariables
  >({
    fetchMore,
    nodes: edges?.map(edge => edge?.node as IShipmentMatchFragment) ?? [],
    pageInfo,
    perPage: PER_PAGE,
    totalCount,
    variables: { carrierId, matchTypes: filters.matchTypes },
  })

  const [loadingFiltered, setLoadingFiltered] = useState<boolean>(false)

  const handleFiltersChanged = async (filters: IMatchingShipmentsFilters) => {
    if (fetchMore) {
      setLoadingFiltered(true)
      await handlePageChange(1)
      await fetchMore({
        variables: {
          carrierId,
          first: PER_PAGE,
          matchTypes: filters.matchTypes,
        },
      })
      setLoadingFiltered(false)
    } else {
      fetchInitial()
    }
  }

  // refetch when filters change
  useEffect(() => {
    handleFiltersChanged(filters)
  }, [filters])

  const loading = loadingInitial || loadingFiltered

  const TableSpinner = memo(() => {
    return (
      <TableRow>
        <TableCell colSpan={9} className='tc'>
          <div className='tc'>
            <div className='pa6'>
              <Spinner position='relative' />
            </div>
          </div>
        </TableCell>
      </TableRow>
    )
  })

  const TableEmpty = memo(() => {
    return (
      <TableRow>
        <TableCell colSpan={9} className='tc'>
          <div className='tc'>
            <div className='pa6'>
              <div className='mb4'>No results found.</div>
            </div>
          </div>
        </TableCell>
      </TableRow>
    )
  })

  return (
    <section className='mt4'>
      <MatchingShipmentsFilters filters={filters} setFilter={setFilter} />

      <Table stickyHeader>
        <MatchingShipmentsTableHeader />
        <TableBody>
          {(loadingInitial || loadingFiltered) && <TableSpinner />}

          {!(loadingInitial || loadingFiltered) && !paginatedShipments?.length && <TableEmpty />}

          {!loadingFiltered &&
            paginatedShipments?.map(laneCarrierMatch => (
              <MatchingShipmentRow laneCarrierMatch={laneCarrierMatch} key={laneCarrierMatch.id} />
            ))}
        </TableBody>
      </Table>

      <Box className='tc'>
        <Pagination
          currentPage={currentPage}
          displayPageNumbers={false}
          loading={loading}
          numberOfPages={totalPages}
          onPageChange={handlePageChange}
        />
      </Box>
    </section>
  )
}

export default MatchingShipmentsList
